import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Container,
  InputAdornment,
  FormControl,
  Select,
  CircularProgress,
  MenuItem,
  styled
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Person,
  Description,
  AttachMoney,
  TrendingUp,
  Receipt
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import useCurrencyConversion from '../../hooks/useCurrencyConversion';
import axios from 'axios';
import { url } from '../utils/constants';

// Styled Components
const ModernButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
  borderRadius: 12,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  color: '#FFFFFF !important',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-2px)',
    color: '#FFFFFF !important',
  },
  '& .MuiButton-endIcon': {
    color: '#FFFFFF !important',
  },
  '& .MuiButton-startIcon': {
    color: '#FFFFFF !important',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.6) !important',
  },
  transition: 'all 0.3s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #757575 0%, #616161 100%)',
  borderRadius: 12,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  color: '#FFFFFF !important',
  boxShadow: '0 4px 12px rgba(117, 117, 117, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #616161 0%, #424242 100%)',
    boxShadow: '0 6px 16px rgba(117, 117, 117, 0.4)',
    transform: 'translateY(-2px)',
    color: '#FFFFFF !important',
  },
  '& .MuiButton-startIcon': {
    color: '#FFFFFF !important',
  },
  '& .MuiButton-endIcon': {
    color: '#FFFFFF !important',
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.6) !important',
  },
  transition: 'all 0.3s ease',
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FAFAFA',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1976D2',
    },
  },
}));

const PixStep2 = () => {
  const { isLoggedIn, authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    convertCurrency,
    calculateServiceFee,
    calculateTotalWithFee,
    formatCurrency,
    getCurrencyInfo,
    serviceFee,
    serviceFeeActive,
    selectedCurrencyPair,
    getCurrentExchangeRate,
    getCurrencyPairInfo,
    loadCompanyFeeSettings
  } = useCurrencyConversion();

  const [formData, setFormData] = useState({
    clientData: ''
  });

  const [calculatedValues, setCalculatedValues] = useState({
    originalValue: 0,
    convertedValue: 0,
    serviceFeeAmount: 0,
    totalWithFee: 0,
    pairInfo: null
  });

  const [loading, setLoading] = useState(false);
  const [step1Data, setStep1Data] = useState(null);

  // Verificar autenticação e dados da etapa anterior
  useEffect(() => {
    if (authLoading) return;

    if (!isLoggedIn) {
      navigate('/signin');
      return;
    }

    if (!location.state?.formData) {
      navigate('/pix');
      return;
    }

    setStep1Data(location.state.formData);

    // Carregar configurações de taxas da empresa
    const token = localStorage.getItem('token');
    if (token) {
      loadCompanyFeeSettings(token);
    }
  }, [isLoggedIn, authLoading, navigate, location.state, loadCompanyFeeSettings]);

  // Escutar eventos de atualização das configurações de taxas
  useEffect(() => {
    const handleFeeSettingsUpdated = () => {
      console.log('🔄 Evento feeSettingsUpdated recebido, recarregando configurações...');
      const token = localStorage.getItem('token');
      if (token) {
        loadCompanyFeeSettings(token);
      }
    };

    window.addEventListener('feeSettingsUpdated', handleFeeSettingsUpdated);

    return () => {
      window.removeEventListener('feeSettingsUpdated', handleFeeSettingsUpdated);
    };
  }, [loadCompanyFeeSettings]);

  // Calcular valores automaticamente
  useEffect(() => {
    if (step1Data?.value && step1Data?.currencyPair) {
      const inputValue = parseFloat(step1Data.value) || 0;

      try {
        console.log('🧮 Calculando conversão:', {
          inputValue,
          currencyPair: step1Data.currencyPair
        });

        // Converter usando o par de moeda selecionado
        const convertedValue = convertCurrency(inputValue, step1Data.currencyPair);
        console.log('💰 Valor convertido:', convertedValue);

        // Obter informações do par de moeda
        const pairInfo = getCurrencyPairInfo();
        console.log('🔄 Par de moeda:', pairInfo);

        // Calcular taxa de serviço na moeda de destino
        const serviceFeeAmount = calculateServiceFee(convertedValue, pairInfo.to);
        console.log('💸 Taxa de serviço:', serviceFeeAmount);

        // Total com taxa
        const totalWithFee = calculateTotalWithFee(convertedValue, pairInfo.to);
        console.log('📊 Total com taxa:', totalWithFee);

        setCalculatedValues({
          originalValue: inputValue,
          convertedValue: convertedValue,
          serviceFeeAmount: serviceFeeAmount,
          totalWithFee: totalWithFee,
          pairInfo: pairInfo
        });
      } catch (error) {
        console.error('Erro ao calcular conversão:', error);
        toast.error('Erro ao calcular conversão de moeda');
      }
    }
  }, [step1Data, convertCurrency, calculateServiceFee, calculateTotalWithFee, getCurrencyPairInfo]);

  // Recalcular quando as configurações de taxas forem carregadas
  useEffect(() => {
    if (step1Data?.value && step1Data?.currencyPair && serviceFee !== null) {
      const inputValue = parseFloat(step1Data.value) || 0;

      try {
        console.log('🔄 Recalculando com configurações carregadas:', {
          inputValue,
          currencyPair: step1Data.currencyPair,
          serviceFee,
          serviceFeeActive
        });

        // Converter usando o par de moeda selecionado
        const convertedValue = convertCurrency(inputValue, step1Data.currencyPair);
        console.log('💰 Valor convertido:', convertedValue);

        // Obter informações do par de moeda
        const pairInfo = getCurrencyPairInfo();
        console.log('🔄 Par de moeda:', pairInfo);

        // Calcular taxa de serviço na moeda de destino
        const serviceFeeAmount = calculateServiceFee(convertedValue, pairInfo.to);
        console.log('💸 Taxa de serviço:', serviceFeeAmount, 'Ativa:', serviceFeeActive);

        // Total com taxa
        const totalWithFee = calculateTotalWithFee(convertedValue, pairInfo.to);
        console.log('📊 Total com taxa:', totalWithFee);

        setCalculatedValues({
          originalValue: inputValue,
          convertedValue: convertedValue,
          serviceFeeAmount: serviceFeeAmount,
          totalWithFee: totalWithFee,
          pairInfo: pairInfo
        });
      } catch (error) {
        console.error('Erro ao recalcular conversão:', error);
        toast.error('Erro ao recalcular conversão de moeda');
      }
    }
  }, [serviceFee, serviceFeeActive, step1Data, convertCurrency, calculateServiceFee, calculateTotalWithFee, getCurrencyPairInfo]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Token de autenticação não encontrado');
        navigate('/signin');
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`
      };

      // Preparar dados para a API (formato exato esperado pela API)
      const clientNumber = formData.clientData || '00000000000';
      const formattedNumber = clientNumber.replace(/[()\s+-]/g, ""); // Remover caracteres especiais

      // Formatar valor como string com vírgula (ex: "10,00")
      const valorFormatado = (calculatedValues.totalWithFee || 0).toFixed(2).replace('.', ',');

      const body = {
        numero: formattedNumber,
        valor: valorFormatado, // Valor como string com vírgula: "5,15"
        descricao: `PIX ${step1Data.currencyPair} - Conversão automática`,
        nomeCompleto: formData.clientData ? `Cliente ${formData.clientData}` : 'Cliente PIX',
        paymentType: 'pix',
        // Dados de taxas e conversão
        originalAmount: calculatedValues.originalValue,
        fromCurrency: calculatedValues.pairInfo?.from,
        toCurrency: calculatedValues.pairInfo?.to,
        exchangeRate: getCurrentExchangeRate()?.rate,
        serviceFee: calculatedValues.serviceFeeAmount,
        totalWithFee: calculatedValues.totalWithFee
      };

      // Validação igual ao pagamentoMercadoPago.js
      if (body.numero.length < 10 || body.valor === '' || body.descricao === '' || body.nomeCompleto === '') {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }

      console.log('📤 Enviando dados para API:', body);
      console.log('🔍 Debug dos valores calculados:', {
        step1Value: step1Data.value,
        step1CurrencyPair: step1Data.currencyPair,
        calculatedValues,
        totalWithFee: calculatedValues.totalWithFee,
        valorFormatado,
        formattedNumber,
        numeroLength: body.numero.length
      });

      // Chamar a API real para criar o pagamento
      const { data: createResponse } = await axios.post(url + '/api/v1/pagamento', body, { headers });

      console.log('✅ PIX criado com sucesso:', createResponse);

      // Extrair ID do pagamento criado
      const paymentId = createResponse.id || createResponse.data?.id;

      if (!paymentId) {
        throw new Error('ID do pagamento não retornado pela API');
      }

      console.log('🆔 ID do pagamento:', paymentId);

      // Buscar dados completos do pagamento
      const { data: paymentData } = await axios.get(url + `/api/v1/pagamento/${paymentId}`, { headers });

      console.log('📋 Dados completos do pagamento:', paymentData);

      // Extrair dados da resposta completa (paymentData tem estrutura {data: {...}})
      const actualData = paymentData.data || paymentData;
      const pixCode = actualData.pixCopyPaste || actualData.pixCode || actualData.qrCode || actualData.code;
      const pixId = actualData.id || actualData._id;
      const pixStatus = actualData.status || 'PENDENTE';
      const pixAmount = actualData.amount || actualData.valor;
      const pixDescription = actualData.description || actualData.descricao;

      console.log('📋 Dados extraídos do pagamento:', {
        actualData,
        pixCode,
        pixId,
        pixStatus,
        pixAmount,
        pixDescription
      });

      // Navegar para a terceira tela com TODOS os dados da API
      navigate('/pix/step3', {
        state: {
          step1Data,
          step2Data: formData,
          calculatedValues,
          // Dados completos da API
          pixData: {
            id: pixId,
            code: pixCode,
            status: pixStatus,
            amount: pixAmount,
            description: pixDescription,
            originalData: actualData // Dados reais da API (sem wrapper)
          },
          pixCode: pixCode
        }
      });

    } catch (error) {
      console.error('❌ Erro ao criar PIX:', error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });

      if (error.response?.data?.message) {
        toast.error(`Erro: ${error.response.data.message}`);
      } else {
        toast.error('Erro ao criar PIX. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/pix', { state: { formData: step1Data } });
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (!step1Data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Box textAlign="center">
          <CircularProgress size={60} sx={{ mb: 2, color: '#1976D2' }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Carregando dados...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aguarde enquanto processamos as informações
          </Typography>
        </Box>
      </Box>
    );
  }

  const currencyInfo = getCurrencyInfo(step1Data.currency);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#FFFFFA',
      padding: 2,
      pt: { xs: '10px', md: '50px' },
      pb: { xs: '40px', md: '20px' }
    }}>
      <Box maxWidth="lg" mx="auto" mt={{ xs: 8, sm: 6 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ mb: 3, background: '#1976D2', color: 'white' }}>
          <Box p={{ xs: 2, sm: 3 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 2, sm: 0 }}
            >
              <Box textAlign={{ xs: 'center', sm: 'left' }}>
                <Typography variant="h6" sx={{
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', md: '1.5rem' }
                }}>
                  Dados do Cliente
                </Typography>
              </Box>
              <Box display="flex" gap={1} width={{ xs: '100%', sm: 'auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    flex: { xs: 1, sm: 'none' }
                  }}
                >
                  VOLVER
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Stepper */}
        <Box mb={4}>
          <Stepper activeStep={1} alternativeLabel>
            <Step>
              <StepLabel>Valor</StepLabel>
            </Step>
            <Step>
              <StepLabel>Cliente</StepLabel>
            </Step>
            <Step>
              <StepLabel>QR Code</StepLabel>
            </Step>
          </Stepper>
        </Box>

        <Grid container spacing={4}>
          {/* Detalhes da Transação */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: '#1976D2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3
                  }}
                >
                  <Receipt />
                  Detalhes da Transação
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Valor Original */}
                <Box display="flex" justifyContent="space-between" mb={3}>
                  <Typography variant="body2" color="text.secondary">
                    Valor Original:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {calculatedValues.pairInfo?.fromSymbol} {(calculatedValues.originalValue || 0).toFixed(2)} {calculatedValues.pairInfo?.from}
                  </Typography>
                </Box>

                {/* Valor Convertido */}
                <Box display="flex" justifyContent="space-between" mb={3}>
                  <Typography variant="body2" color="text.secondary">
                    Valor Convertido:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="error">
                    {calculatedValues.pairInfo?.toSymbol} {(calculatedValues.convertedValue || 0).toFixed(2)} {calculatedValues.pairInfo?.to}
                  </Typography>
                </Box>

                {/* Taxa Serviço - Só exibe se estiver ativa */}
                {serviceFeeActive && (
                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography variant="body2" color="text.secondary">
                      Taxa Serviço:
                    </Typography>
                    <Box textAlign="right">
                      <Typography variant="body2" color="text.secondary">
                        {calculatedValues.pairInfo?.toSymbol} {(calculatedValues.serviceFeeAmount || 0).toFixed(2)} {calculatedValues.pairInfo?.to}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        ({serviceFee}%)
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Cotação */}
                <Box display="flex" justifyContent="space-between" mb={3}>
                  <Typography variant="body2" color="text.secondary">
                    Cotação:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    1 {calculatedValues.pairInfo?.from} = {calculatedValues.pairInfo?.toSymbol} {(getCurrentExchangeRate()?.rate || 0).toFixed(4)} {calculatedValues.pairInfo?.to}
                  </Typography>
                </Box>


                <Divider sx={{ my: 3 }} />

                {/* Valor Total */}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" color="primary">
                    Valor Total:
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {calculatedValues.pairInfo?.toSymbol} {serviceFeeActive ? (calculatedValues.totalWithFee || 0).toFixed(2) : (calculatedValues.convertedValue || 0).toFixed(2)} {calculatedValues.pairInfo?.to}
                  </Typography>
                </Box>

              </CardContent>
            </Card>
          </Grid>

          {/* Dados do Cliente */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <form onSubmit={handleSubmit}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: '#1976D2',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 3
                    }}
                  >
                    <Person />
                    Dados do Cliente
                  </Typography>

                  <Box sx={{ mb: 5 }}>
                    <ModernTextField
                      fullWidth
                      label="CPF/CNPJ do cliente (opcional)"
                      value={formData.clientData}
                      onChange={(e) => handleInputChange('clientData', e.target.value)}
                      placeholder="Favor informar o CPF/CNPJ do cliente"
                      helperText="Campo opcional - informe o documento de identificação do cliente"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Description color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <ModernButton
                    type="submit"
                    size="large"
                    fullWidth
                    disabled={loading}
                    endIcon={<ArrowForward />}
                  >
                    {loading ? 'Gerando PIX...' : 'Gerar PIX'}
                  </ModernButton>
                </form>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Box>
    </Box>
  );
};

export default PixStep2;
